import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users, Plus, Settings as SettingsIcon, Upload, Search, ClipboardList, Phone as PhoneIcon, UserCircle2, X, Archive, ArchiveRestore, BellOff, UserPlus, QrCode, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import AddPatientDialog from "@/components/patients/AddPatientDialog";
import BulkDeleteDialog from "@/components/patients/BulkDeleteDialog";
import ImportPatientsDialog from "@/components/patients/ImportPatientsDialog";
import PatientSignUpLinkDialog from "@/components/patients/PatientSignUpLinkDialog";
import PublicOnboardingLinkPanel from "@/components/public-link/PublicOnboardingLinkPanel";
import MvCaseImportDialog from "@/components/treatment/MvCaseImportDialog";
import { useFeatures } from "@/context/FeaturesContext";
import { useOngoingTreatmentRollout } from "@/context/OngoingTreatmentRolloutContext";
import { formatDoctorName } from "@/lib/doctorName";

// =====================================================================
// Patients — Phase 5 R2B
// Adds Manage mode + bulk delete on top of R1.
// =====================================================================

const STATUS_TONES = {
  New:      "bg-violet-50 text-violet-700 border-violet-200",
  Active:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  Inactive: "bg-slate-100 text-slate-600 border-slate-200",
  Archived: "bg-rose-50 text-rose-700 border-rose-200",
};

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}
function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return d; }
}
function formatMoney(centavos, currency) {
  const symbol = currency === "PHP" ? "₱"
              : currency === "USD" ? "$"
              : currency === "INR" ? "₹"
              : currency ? `${currency} ` : "";
  return symbol + Math.round((centavos ?? 0) / 100).toLocaleString("en-US");
}

export default function Patients() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientRegistrationTutorialDemo =
    searchParams.get("tutorial") === "patient-registration"
    && searchParams.get("demo") === "1";
  const { activeClinic, activeClinicId, clinicRole } = useAuth();
  const { access } = useFeatures();
  const { enabled: ongoingTreatmentEnabled } = useOngoingTreatmentRollout();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch]           = useState("");
  const [addOpen, setAddOpen]         = useState(false);
  const [importOpen, setImportOpen]   = useState(false);
  const [manageMode, setManageMode]   = useState(false);
  const [selected, setSelected]       = useState(new Set());
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [selfRegOnly, setSelfRegOnly] = useState(false);
  const [signUpOpen, setSignUpOpen]   = useState(false);
  const [patientView, setPatientView] = useState("active");
  const [reactivating, setReactivating] = useState(false);
  const [mvImportPatient, setMvImportPatient] = useState(null);

  useEffect(() => {
    if (!ongoingTreatmentEnabled) setMvImportPatient(null);
  }, [ongoingTreatmentEnabled]);

  const billingReadOnly = access?.mode === "read_only";
  const canCreate = clinicRole && clinicRole !== "assistant" && !billingReadOnly;
  const canManage = clinicRole === "clinic_admin" && !billingReadOnly;
  const currency  = activeClinic?.currency || "PHP";

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async (q, view = patientView) => {
    setLoading(true);
    setError(null);
    const includeArchived = view === "archived";
    const { data, error: err } = await supabase.rpc("list_patients", {
      p_search: q || null,
      p_include_archived: includeArchived,
    });
    setLoading(false);
    if (err) { setError(err.message ?? "Could not load patients."); setRows([]); return; }
    const nextRows = data ?? [];
    setRows(includeArchived ? nextRows.filter((r) => r.status === "Archived") : nextRows);
  }, [patientView]);

  useEffect(() => { if (activeClinicId) load(search, patientView); }, [activeClinicId, search, patientView, load]);

  const total = rows.length;
  const isSearching = search.length > 0;
  const existingPhones = useMemo(() => rows.map((r) => r.phone).filter(Boolean), [rows]);
  const selfRegCount = useMemo(
    () => rows.filter((r) => r.registration_source === "self_registration" && r.self_registered_verified === false).length,
    [rows]
  );
  const visibleRows = useMemo(
    () => (selfRegOnly
      ? rows.filter((r) => r.registration_source === "self_registration" && r.self_registered_verified === false)
      : rows),
    [rows, selfRegOnly]
  );

  // Manage-mode helpers
  function enterManage()  { setManageMode(true);  setSelected(new Set()); }
  function exitManage()   { setManageMode(false); setSelected(new Set()); }
  function changePatientView(view) {
    setPatientView(view);
    setManageMode(false);
    setSelected(new Set());
    setSelfRegOnly(false);
  }
  function toggleRow(id)  {
    setSelected((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }
  function selectAll()    { setSelected(new Set(rows.map((r) => r.id))); }
  function deselectAll()  { setSelected(new Set()); }
  async function handleReactivateSelected() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setReactivating(true);
    const { data, error: err } = await supabase.rpc("reactivate_archived_patients", { p_ids: ids });
    setReactivating(false);
    if (err) {
      toast.error(err.message ?? "Could not reactivate patients.");
      return;
    }
    const n = data?.reactivated_count ?? ids.length;
    toast.success(`${n} ${n === 1 ? "patient" : "patients"} reactivated`);
    exitManage();
    load(search, patientView);
  }

  const selectedCount = selected.size;
  const allSelected   = total > 0 && selectedCount === total;

  function handlePatientCreated(created) {
    toast.success("Patient created");
    load(search, patientView);
  }

  return (
    <div className="space-y-6" data-testid="patients-page" data-tutorial-id="patients-page">
      {/* Header */}
      <header className="flex flex-wrap items-start gap-4 justify-between" data-testid="patients-header">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-teal-600" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="patients-title">Patients</h1>
            <p className="text-xs text-slate-500 mt-1">
              <span data-testid="patients-count">{loading ? "…" : total}</span>
              {total === 1 ? " patient" : " patients"} {patientView === "archived" ? "archived" : "active"} in {activeClinic?.clinic_name ?? "this clinic"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" data-testid="patients-actions" data-tutorial-id="patients-actions">
          {!manageMode ? (
            <>
              <button
                type="button"
                onClick={() => setSignUpOpen(true)}
                data-testid="patients-signup-link-button"
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
                title="Show the patient self-registration QR / link"
              >
                <QrCode className="w-4 h-4" /> Sign-Up Link
              </button>
              <button
                type="button"
                onClick={enterManage}
                disabled={!canManage || total === 0}
                data-testid="patients-manage-button"
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                title={!canManage ? "Only clinic admins can manage patients" : (total === 0 ? "No patients to manage" : "Enter manage mode")}
              >
                <SettingsIcon className="w-4 h-4" /> Manage
              </button>
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                disabled={!canCreate}
                data-testid="patients-import-button"
                data-tutorial-id="patients-import-button"
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                title={!canCreate ? "Your role can't add patients" : "Import patients from a CSV file"}
              >
                <Upload className="w-4 h-4" /> Import CSV
              </button>
              {canCreate ? (
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  data-testid="patients-add-button"
                  data-tutorial-id="patients-new-button"
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Add Patient
                </button>
              ) : (
                <button type="button" disabled data-testid="patients-add-button-disabled"
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg bg-slate-100 text-slate-400 cursor-not-allowed"
                  title="Your role can't add patients">
                  <Plus className="w-4 h-4" /> Add Patient
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={allSelected ? deselectAll : selectAll}
                data-testid="patients-select-all-button"
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
              >
                {allSelected ? "Deselect all" : "Select all"}
              </button>
              <button
                type="button"
                onClick={exitManage}
                data-testid="patients-cancel-manage-button"
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
              {patientView === "archived" ? (
                <button
                  type="button"
                  onClick={handleReactivateSelected}
                  disabled={selectedCount === 0 || reactivating}
                  data-testid="patients-reactivate-button"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-sm disabled:bg-teal-300 disabled:cursor-not-allowed"
                >
                  <ArchiveRestore className="w-4 h-4" /> {reactivating ? "Reactivating..." : `Reactivate (${selectedCount})`}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  disabled={selectedCount === 0}
                  data-testid="patients-bulk-delete-button"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors shadow-sm disabled:bg-rose-300 disabled:cursor-not-allowed"
                >
                  <Archive className="w-4 h-4" /> Archive ({selectedCount})
                </button>
              )}
            </>
          )}
        </div>
      </header>

      <PublicOnboardingLinkPanel surface="patients" />

      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1" data-testid="patients-view-switch">
        <button
          type="button"
          onClick={() => changePatientView("active")}
          data-testid="patients-view-active"
          className={`px-3 py-1.5 text-sm font-medium rounded-md ${patientView === "active" ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
        >
          Active
        </button>
        <button
          type="button"
          onClick={() => changePatientView("archived")}
          data-testid="patients-view-archived"
          className={`px-3 py-1.5 text-sm font-medium rounded-md ${patientView === "archived" ? "bg-teal-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"}`}
        >
          Inactive/Archived
        </button>
      </div>

      {/* Selection bar (manage mode only) */}
      {manageMode ? (
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-2.5 flex items-center justify-between text-sm" data-testid="patients-selection-bar">
          <div className="text-violet-900">
            <span data-testid="patients-selection-count">{selectedCount}</span> of {total} selected
          </div>
          <div className="flex items-center gap-3 text-xs">
            <button type="button" onClick={selectAll}   data-testid="patients-selection-all"      className="font-medium text-violet-700 hover:text-violet-900 underline-offset-2 hover:underline">Select all</button>
            <button type="button" onClick={deselectAll} data-testid="patients-selection-deselect" className="font-medium text-violet-700 hover:text-violet-900 underline-offset-2 hover:underline">Deselect all</button>
          </div>
        </div>
      ) : null}

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm p-3" data-testid="patients-search-card" data-tutorial-id="patients-search-bar">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="patients-search-input"
            placeholder="Search by name or phone…"
            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
          />
        </div>
      </div>

      {/* Filters */}
      {patientView === "active" && selfRegCount > 0 ? (
        <div className="flex items-center gap-2" data-testid="patients-filters">
          <button
            type="button"
            onClick={() => setSelfRegOnly((v) => !v)}
            data-testid="patients-filter-selfreg"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition ${selfRegOnly ? "bg-sky-600 text-white border-sky-600" : "bg-white text-sky-700 border-sky-200 hover:bg-sky-50"}`}
          >
            <UserPlus className="w-3.5 h-3.5" /> Self-registered to review ({selfRegCount})
          </button>
        </div>
      ) : null}

      {/* Body */}
      {error ? (
        <div data-testid="patients-error" className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700">{error}</div>
      ) : loading ? (
        <div data-testid="patients-loading" className="text-sm text-slate-400">Loading patients…</div>
      ) : total === 0 ? (
        <EmptyState searching={isSearching} canCreate={canCreate} patientView={patientView} onAdd={() => setAddOpen(true)} />
      ) : (
        <ul className="space-y-2" data-testid="patients-list">
          {visibleRows.map((p) => (
            <PatientRow
              key={p.id}
              p={p}
              currency={currency}
              manageMode={manageMode}
              checked={selected.has(p.id)}
              onToggle={() => toggleRow(p.id)}
              onOpen={() => navigate(`/patients/${p.id}`)}
            />
          ))}
        </ul>
      )}

      {/* Dialogs */}
      {canCreate ? (
        <>
          <AddPatientDialog
            open={addOpen}
            onOpenChange={setAddOpen}
            existingPhones={existingPhones}
            tutorialDemo={patientRegistrationTutorialDemo}
            onCreated={handlePatientCreated}
          />
          <ImportPatientsDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            existingPhones={existingPhones}
            onImported={(n) => {
              toast.success(`${n} ${n === 1 ? "patient" : "patients"} imported`);
              load(search, patientView);
            }}
          />
        </>
      ) : null}

      <BulkDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        patientIds={Array.from(selected)}
        onDeleted={(result) => {
          const n = result?.archived_count ?? result?.deleted_count ?? selected.size;
          toast.success(`${n} ${n === 1 ? "patient" : "patients"} archived`);
          exitManage();
          load(search, patientView);
        }}
      />

      <PatientSignUpLinkDialog open={signUpOpen} onOpenChange={setSignUpOpen} />
      {false ? (
        <MvCaseImportDialog
          open={!!mvImportPatient}
          patientId={mvImportPatient?.id}
          patientName={mvImportPatient?.full_name}
          sourceEntryPoint="patient_created"
          onClose={() => setMvImportPatient(null)}
          onImported={() => load(search, patientView)}
        />
      ) : null}
    </div>
  );
}

// ---- presentational ------------------------------------------------
function PatientRow({ p, currency, manageMode, checked, onToggle, onOpen }) {
  const handleClick = manageMode ? onToggle : onOpen;
  return (
    <li
      data-testid={`patient-row-${p.id}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); } }}
      className={`bg-white rounded-xl shadow-sm p-4 flex items-center gap-4 cursor-pointer hover:shadow-md hover:border-teal-200 border transition-all ${checked ? "border-violet-400 ring-2 ring-violet-100" : "border-transparent"}`}
    >
      {manageMode ? (
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          data-testid={`patient-checkbox-${p.id}`}
          className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 shrink-0"
        />
      ) : null}
      <div className="w-12 h-12 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center shrink-0 font-semibold text-sm" data-testid={`patient-avatar-${p.id}`}>
        {initials(p.full_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 truncate" data-testid={`patient-name-${p.id}`}>{p.full_name || "Unnamed"}</span>
          <span data-testid={`patient-status-${p.id}`} className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${STATUS_TONES[p.status] || STATUS_TONES.New}`}>{p.status}</span>
          {p.registration_source === "self_registration" && p.self_registered_verified === false ? (
            <span data-testid={`patient-selfreg-${p.id}`} title="Registered themselves via the sign-up link — review and verify" className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center gap-1">
              <UserPlus className="w-3 h-3" /> Self-registered
            </span>
          ) : null}
          {p.has_medical_history ? (
            <span data-testid={`patient-medhistory-${p.id}`} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1">
              <ClipboardList className="w-3 h-3" /> Med history
            </span>
          ) : null}
          {p.recall_opt_out ? (
            <span data-testid={`patient-recall-off-${p.id}`} title="Patient has opted out of recall outreach (DPA)" className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 inline-flex items-center gap-1">
              <BellOff className="w-3 h-3" /> Recall off
            </span>
          ) : null}
          {p.possible_match_pending ? (
            <span data-testid={`patient-possible-match-${p.id}`} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> possible match - review
            </span>
          ) : null}
        </div>
        <div className="mt-1 flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-slate-500">
          <span className="inline-flex items-center gap-1" data-testid={`patient-phone-${p.id}`}>
            <PhoneIcon className="w-3 h-3" /> {p.phone || "—"}
          </span>
          <span className="inline-flex items-center gap-1" data-testid={`patient-doctor-${p.id}`}>
            <UserCircle2 className="w-3 h-3" />
            {p.preferred_doctor_name ? formatDoctorName(p.preferred_doctor_name) : "No preferred doctor"}
          </span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-bold text-amber-600" data-testid={`patient-otv-${p.id}`}>{formatMoney(p.pending_treatment_centavos, currency)}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">Pending</div>
        <div className="text-[11px] text-slate-500 mt-1" data-testid={`patient-lastactivity-${p.id}`}>{formatDate(p.last_activity_date)}</div>
      </div>
    </li>
  );
}

function EmptyState({ searching, canCreate, patientView, onAdd }) {
  if (searching) {
    return (
      <div data-testid="patients-empty-search" className="bg-white rounded-xl shadow-sm p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 mx-auto flex items-center justify-center"><Search className="w-5 h-5 text-slate-400" /></div>
        <h3 className="mt-4 text-base font-semibold text-slate-900">No patients match your search.</h3>
        <p className="text-sm text-slate-500 mt-1">Try a different name or phone fragment.</p>
      </div>
    );
  }
  if (patientView === "archived") {
    return (
      <div data-testid="patients-empty-archived" className="bg-white rounded-xl shadow-sm p-10 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 mx-auto flex items-center justify-center"><Archive className="w-5 h-5 text-slate-500" /></div>
        <h3 className="mt-4 text-base font-semibold text-slate-900">No inactive or archived patients.</h3>
        <p className="text-sm text-slate-500 mt-1">Archived patients stay hidden from the active list but their records are kept.</p>
      </div>
    );
  }
  return (
    <div data-testid="patients-empty" className="bg-white rounded-xl shadow-sm p-10 text-center">
      <div className="w-12 h-12 rounded-full bg-teal-50 mx-auto flex items-center justify-center"><Users className="w-5 h-5 text-teal-600" /></div>
      <h3 className="mt-4 text-base font-semibold text-slate-900">No patients yet.</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">Add your first patient to start booking appointments and running checkouts.</p>
      {canCreate ? (
        <button type="button" onClick={onAdd} data-testid="patients-empty-add-button"
          className="mt-5 inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors shadow-sm">
          <Plus className="w-4 h-4" /> Add Patient
        </button>
      ) : null}
    </div>
  );
}
